import { useTranslation } from "react-i18next";
import {
  Wallet,
  Pulse,
  ChartLineUp,
  ArrowsLeftRight,
  Coins,
  Lightning,
  ClockCounterClockwise,
  ArrowsClockwise,
  Receipt,
  PaperPlaneTilt,
  ShieldWarning,
  PlugsConnected,
  Signature,
  Cube,
  Info,
  Handshake,
  FileCode,
  SealCheck,
  Eye,
} from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";

const showcaseFeatures = [
  {
    key: "walletOverview",
    image: "/images/feature-wallet-overview.webp",
    accentColor: "violet",
    sectionIcon: Wallet,
    microIcons: [ArrowsLeftRight, ChartLineUp, Coins, Lightning],
  },
  {
    key: "transactionTracking",
    image: "/images/feature-transaction-tracking.webp",
    accentColor: "cyan",
    sectionIcon: Pulse,
    microIcons: [ClockCounterClockwise, ChartLineUp, ArrowsClockwise, Receipt],
  },
  {
    key: "web3History",
    image: "/images/feature-web3-history.webp",
    accentColor: "red",
    sectionIcon: PaperPlaneTilt,
    microIcons: [Info, SealCheck, Handshake, ArrowsClockwise, Lightning],
  },
  {
    key: "scamProtection",
    image: "/images/feature-scam-protection.webp",
    accentColor: "amber",
    sectionIcon: ChartLineUp,
    microIcons: [Coins, Handshake, Info, ChartLineUp, ChartLineUp],
  },
  {
    key: "connectionProtection",
    image: "/images/feature-connection-protection.webp",
    accentColor: "pink",
    sectionIcon: ShieldWarning,
    microIcons: [FileCode, ShieldWarning, SealCheck, Info, Eye],
  },
  {
    key: "transactionAnalysis",
    image: "/images/feature-transaction-analysis.webp",
    accentColor: "emerald",
    sectionIcon: PlugsConnected,
    microIcons: [ShieldWarning, Info, Handshake, Eye, SealCheck],
  },
  {
    key: "buyCrypto",
    image: "/images/feature-buy-crypto.webp",
    accentColor: "violet",
    sectionIcon: Signature,
    microIcons: [ShieldWarning, SealCheck, FileCode, Info],
  },
  {
    key: "staking",
    image: "/images/feature-staking.webp",
    accentColor: "emerald",
    sectionIcon: Cube,
    microIcons: [ClockCounterClockwise, ShieldWarning, ArrowsClockwise, Info],
  },
];

const accentMap = {
  violet: {
    border: "border-violet-500/30",
    bg: "bg-violet-500/10",
    text: "text-violet-400",
    glow: "rgba(139,92,246,0.15)",
    checkBg: "bg-violet-500/10 border-violet-500/25",
    checkText: "text-violet-300",
  },
  emerald: {
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/10",
    text: "text-emerald-400",
    glow: "rgba(52,211,153,0.15)",
    checkBg: "bg-emerald-500/10 border-emerald-500/25",
    checkText: "text-emerald-300",
  },
  cyan: {
    border: "border-cyan-500/30",
    bg: "bg-cyan-500/10",
    text: "text-cyan-400",
    glow: "rgba(34,211,238,0.15)",
    checkBg: "bg-cyan-500/10 border-cyan-500/25",
    checkText: "text-cyan-300",
  },
  amber: {
    border: "border-amber-500/30",
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    glow: "rgba(245,158,11,0.15)",
    checkBg: "bg-amber-500/10 border-amber-500/25",
    checkText: "text-amber-300",
  },
  pink: {
    border: "border-pink-500/30",
    bg: "bg-pink-500/10",
    text: "text-pink-400",
    glow: "rgba(236,72,153,0.15)",
    checkBg: "bg-pink-500/10 border-pink-500/25",
    checkText: "text-pink-300",
  },
  red: {
    border: "border-red-500/30",
    bg: "bg-red-500/10",
    text: "text-red-400",
    glow: "rgba(239,68,68,0.15)",
    checkBg: "bg-red-500/10 border-red-500/25",
    checkText: "text-red-300",
  },
};

const FeatureShowcase = ({ feature, index, t, isFirst }) => {
  const accent = accentMap[feature.accentColor] || accentMap.violet;
  const isReversed = index % 2 !== 0;
  const microFeatures = t(`featuresPage.showcase.${feature.key}.microFeatures`, { returnObjects: true }) || [];
  const SectionIcon = feature.sectionIcon;
  const microIcons = feature.microIcons || null;

  return (
    <section
      className={`relative min-h-screen flex flex-col justify-center ${index % 2 !== 0 ? 'bg-white/[0.015]' : ''}`}
      data-testid={`feature-showcase-${feature.key}`}
    >
      {/* Hero title only on first section */}
      {isFirst && (
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-6 mb-6 md:mb-10" data-testid="features-hero">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {t("featuresPage.hero.title")}
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t("featuresPage.hero.subtitle")}
          </p>
        </div>
      )}

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16`}>

          {/* Image side */}
          <div className="w-full lg:w-5/12 flex justify-center">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-3xl blur-[60px] -z-10"
                style={{ background: `radial-gradient(circle, ${accent.glow} 0%, transparent 70%)` }}
              />
              <img
                src={feature.image}
                alt={t(`featuresPage.showcase.${feature.key}.title`)}
                className="w-full max-w-[360px] h-auto rounded-2xl"
                width="360"
                height="640"
                loading="eager"
                decoding="async"
                fetchpriority={index < 2 ? "high" : "low"}
              />
            </div>
          </div>

          {/* Text side */}
          <div className="w-full lg:w-7/12">
            <div
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 ${accent.bg} border ${accent.border} rounded-full mb-4`}
              data-testid={`feature-badge-${feature.key}`}
            >
              {SectionIcon && (
                <SectionIcon
                  size={16}
                  weight="duotone"
                  className={`${accent.text} flex-shrink-0`}
                  aria-hidden="true"
                  data-testid={`feature-section-icon-${feature.key}`}
                />
              )}
              <span className={`${accent.text} text-xs font-semibold tracking-wider uppercase leading-none`}>
                {t(`featuresPage.showcase.${feature.key}.badge`)}
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
              {t(`featuresPage.showcase.${feature.key}.title`)}
            </h2>

            <p className="text-gray-400 text-sm lg:text-base leading-relaxed mb-6 max-w-xl">
              {t(`featuresPage.showcase.${feature.key}.description`)}
            </p>

            {microFeatures.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {microFeatures.map((mf, i) => {
                  const BulletIcon = (microIcons && microIcons[i]) || Info;
                  return (
                    <div
                      key={mf}
                      className={`flex items-start gap-3 px-3.5 py-2.5 rounded-xl border ${accent.checkBg} transition-all duration-200 hover:scale-[1.02]`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${accent.bg} border ${accent.border} flex-shrink-0`}
                        aria-hidden="true"
                      >
                        <BulletIcon size={16} weight="duotone" className={accent.text} />
                      </span>
                      <span className={`${accent.checkText} text-sm leading-snug mt-1`}>{mf}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturesPage = () => {
  const { t } = useTranslation();

  return (
    <main role="main" aria-label={t("featuresPage.seo.title")} className="min-h-screen bg-lumina-dark pt-20">
      <SEOHead title={t("featuresPage.seo.title")} description={t("featuresPage.seo.description")} path="/features" />

      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-lumina-violet/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-3xl" />
      </div>

      {/* Feature Showcases — each is a full viewport section */}
      <div className="relative z-10">
        {showcaseFeatures.map((feature, index) => (
          <FeatureShowcase key={feature.key} feature={feature} index={index} t={t} isFirst={index === 0} />
        ))}
      </div>
    </main>
  );
};

export default FeaturesPage;
